import React, { FC, useCallback, useEffect, useMemo } from 'react';
import {
  AppFooter_v2 as AppFooter,
  Avatar_v2 as Avatar,
  Button_v2 as Button,
  Container_v2 as Container,
  IconSuccess,
  Rating,
  Textarea_v2 as Textarea,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';

import { Keyboard } from '@capacitor/keyboard';
import { isPlatform, TextareaChangeEventDetail } from '@ionic/react';
import { IonTextareaCustomEvent } from '@ionic/core';
import { createReview } from '../../redux/specialistReviews/thunk';
import { useDispatch } from 'react-redux';
import useToast from '../../context/ToastContext/ToastContext';
import { InAppReview } from '@capacitor-community/in-app-review';
import { useTranslation } from 'react-i18next';
import { useAuthFlow } from '../../shared';

type ReviewProps = {
  specialistID: string;
  specialistName: string;
  avatar?: string;
  onClose?: () => void;
};

const Review: FC<ReviewProps> = ({ specialistID, specialistName, avatar, onClose }) => {
  const [ratingValue, setRatingValue] = React.useState<number>(0);
  const [reviewText, setReviewText] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isFinished, setIsFinished] = React.useState<boolean>(false);
  const [isKeyboardShown, setIsKeyboardShown] = React.useState<boolean>(false);

  const inputRef = React.useRef<HTMLIonTextareaElement>(null);
  const containerRef = React.useRef<HTMLIonContentElement>(null);

  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { authUser } = useAuthFlow();

  const { t } = useTranslation('reviews');

  useEffect(() => {
    if (!isPlatform('hybrid')) return;

    Keyboard.addListener('keyboardDidShow', () => {
      setIsKeyboardShown(true);
      containerRef?.current?.scrollToBottom();
    });

    Keyboard.addListener('keyboardWillHide', () => {
      setIsKeyboardShown(false);
    });

    return () => {
      if (!isPlatform('hybrid')) return;

      Keyboard.removeAllListeners();
    };
  }, [containerRef]);

  const isButtonDisabled = useMemo(() => !(ratingValue > 0 && reviewText.length > 0), [ratingValue, reviewText]);

  const submitReview = useCallback(async () => {
    try {
      setIsLoading(true);
      const clientID = authUser?.uid;

      await dispatch(
        createReview({
          specialistID,
          clientID: clientID ?? '',
          textOfReview: reviewText,
          starsCount: ratingValue,
        })
      );
      setIsFinished(true);
    } catch (error) {
      showToast({ error });
    } finally {
      setIsLoading(false);
    }
  }, [ratingValue, reviewText, specialistID, authUser?.uid]);

  const closeHandle = useCallback(async () => {
    if (ratingValue === 5 && isPlatform('hybrid')) {
      await InAppReview.requestReview();
    }
    onClose?.();
  }, [onClose, ratingValue]);

  const handleRatingChange = useCallback(
    async (val: number) => {
      setRatingValue(val);
      if (inputRef.current) {
        await inputRef.current.setFocus();
      }
    },
    [inputRef]
  );

  const handleInputChange = useCallback(
    (e: IonTextareaCustomEvent<TextareaChangeEventDetail>) => {
      setReviewText(e.detail.value ?? '');
      containerRef?.current?.scrollToBottom();
    },
    [containerRef]
  );

  return (
    <>
      <Container ref={containerRef}>
        <div className={'flex flex-col h-full'}>
          <div className={`flex-1 flex flex-col justify-center items-center pb-6 ${isFinished ? 'gap-2' : 'gap-3'}`}>
            {isFinished ? (
              <>
                <IconSuccess />
                <Typography variant={TypographyVariantsEnum.Title28Semi} align={'center'}>
                  {t('reviews.good.review.sent.title', { specialistName })}
                </Typography>
                <Typography variant={TypographyVariantsEnum.Text14Regular} align={'center'}>
                  {ratingValue === 5 ? t('reviews.good.review.sent.description') : t('reviews.review.sent.description')}
                </Typography>
              </>
            ) : (
              <>
                <Avatar src={avatar ?? ''} variant={'medium'} />
                <Typography variant={TypographyVariantsEnum.Title24Semi} align={'center'}>
                  {t('reviews.add.review.title', { specialistName })}
                </Typography>
                <Typography variant={TypographyVariantsEnum.Text14Regular} align={'center'}>
                  {t('reviews.add.review.description')}
                </Typography>
                <Rating initialRating={ratingValue} onChange={handleRatingChange} />
              </>
            )}
          </div>
          {!isFinished && (
            <Textarea
              ref={inputRef}
              borderTop
              showCounter
              autoGrow
              minlength={1}
              maxlength={1500}
              rows={1}
              onIonChange={handleInputChange}
              placeholder={t('reviews.add.review.placeholder')}
            />
          )}
        </div>
      </Container>
      <AppFooter transparent bottomSaveArea={!isKeyboardShown}>
        {isFinished ? (
          <Button size={'large'} fullWidth onClick={closeHandle}>
            {ratingValue === 5 ? t('reviews.good.review.sent.rate.button') : t('reviews.done.button')}
          </Button>
        ) : (
          <Button size={'large'} fullWidth disabled={isButtonDisabled} isLoading={isLoading} onClick={submitReview}>
            {t('reviews.add.review.send.button')}
          </Button>
        )}
      </AppFooter>
    </>
  );
};

export default Review;
